import { createSpawnSourceReaders as nativeSpawnSourceReaders, previewInventoryFromSources, planEncounterInventorySpawn, spawnCreatureSourceKey, spawnCreatureTableInputsMissing, type SpawnSourcePackage } from '../janus-engine/spawn/source-plan';
import { planCreatureSpawn, planSummonRows, planTransformRows, planLairRows, mergeSpawnPlans, emptySpawnPlan, spawnPlanHasEffects, spawnRowIdentity, transformKitItemIds, storedSpawnPoint, type CreatureSpawnRequest, type Row, type SpawnRowPlan } from '../janus-engine/spawn/row-plan';
import { admitSpawnRowPlan } from '../janus-engine/spawn/row-value-contract';
import { definitionVitalityMaximum } from '../janus-engine/spawn/vitality';
import { fromBaseClone, fromBaseRecord, fromBaseUuid } from '../janus-engine/compile/from-base-context';
import { refuseUnported } from '../janus-engine/unported';
import { surfaceField } from '../janus-engine/compile/from-base-encounter-media';
import { compileFetchedEntity } from './compile-fetched-entity';
import { heldEncounterForAction } from './entity-compile-ready';
import { browserRollSource } from '../janus-engine/actions/roll-source';
import type { JanusReaders } from '../janus-engine/ability/context';
import { ecId, ecLean, handBack, HandBack } from '../janus-engine/ability/context';
import { encounterField } from './array-types';
import { readInventoryField, readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers';
import { inventoryCreationSizeTemplateIds } from '../janus-engine/ability/timed-finish/inventory';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { deathSourceIds, DEATH_STATUS_SOURCE_IDS } from '../janus-engine/death/compose';
import { resumePlan } from '../janus-engine/actions/deferred-plan';
import type { SpawnPreviewInput } from '../janus-engine/spawn/preview';
import { withSpawnEntities } from '../janus-engine/spawn/applied-entities';
import { STATUS_IDS } from '../janus-engine/statuses/expiry';
import { INVENTORY_HIDDEN_ID } from '../janus-engine/inventory/hidden-plan';
import { planIceWallSections } from '../janus-engine/ability/aoe/painted-wall-ice-cast';

import { spawnAppliedFrame } from '../janus-engine/transport/gm-applied-frame';
import { saveAppliedTruth } from './applied-truth-transport';
import { beginSpawnSave } from './spawn-save-pending';
import { SPOILING_STATUS_ID } from '../janus-engine/inventory/birth-inputs-20';
import { _ftCamField } from './campaign-field';
import { withAsyncDeadline } from './async-deadline';
import { createEquipmentPreviewSession } from './equipment-preview-session';

import { inventoryGrantSourceIds, inventoryIdentitySpawnReaders } from '../janus-engine/compile/inventory-delivery-20';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import { heldMemberDefinitionInputs } from '../janus-engine/compile/member-status-inputs';
import { inventoryYieldTemplateSources } from '../janus-engine/inventory/yield-template-sources';
import { ftCurrentTruthSink } from './truth-sink';
import { appendInitialInventoryStatus } from '../janus-engine/inventory/initial-status-plan';
import { planObjectSize, planCurtainSize } from '../janus-engine/inventory/object-size-plan';
import type { InventorySpawnPlacement } from '../janus-engine/spawn/inventory-placement';
declare global { interface Window { ftNewInventoryPlacementInputs?: (request: Row) => Promise<InventorySpawnPlacement> } }
const createSpawnSourceReaders: typeof nativeSpawnSourceReaders = (data, ...args) => inventoryIdentitySpawnReaders(
    nativeSpawnSourceReaders(data, ...args), Object.keys(data.shared).length ? mergeInventoryMetadata(data.shared, data.base) : data.base,
    entityRuntimeMetadata(data.metadata).inventory);

const uuid = (): string => crypto.randomUUID().toUpperCase();
const creatorId = (): string => String((window as unknown as { FateTide?: { user?: { id?: unknown } } }).FateTide?.user?.id ?? '');
const die = (sides: number): number => browserRollSource().d(sides);
const sourceIds = (node: unknown, result = new Set<string>()): Set<string> => {
    if (typeof node === 'string') for (const part of node.split('|')) { if (fromBaseUuid(part)) result.add(part); }
    else if (node && typeof node === 'object') for (const value of Object.values(node)) sourceIds(value, result);
    return result;
};
function spawnSourceGeneration(catalogVersion: unknown = null): string {
    const store = (globalThis as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
    return JSON.stringify([catalogVersion, store?.epoch ?? null, store?.cacheGeneration ?? null]);
}
/** The source cache belongs to the page: spawn-browser is also included in other bundles. */
function cachedSpawnSources(key: string, load: () => Promise<SpawnSourcePackage>): Promise<SpawnSourcePackage> {
    const host = globalThis as { __ftSpawnSourcePackages?: Map<string, Promise<SpawnSourcePackage>> };
    const packages = host.__ftSpawnSourcePackages ??= new Map<string, Promise<SpawnSourcePackage>>();
    let loading = packages.get(key);
    if (!loading) {
        loading = Promise.resolve().then(load);
        packages.set(key, loading);
        const pending = loading;
        void pending.catch(() => { if (packages.get(key) === pending) packages.delete(key); });
    }
    return loading.then(data => fromBaseClone(data));
}
export async function loadSpawnSources(ids: readonly string[], templates: readonly string[] = [], draft?: Row, destinationId?: string, includeGrantIdentities = true, enhancements: readonly string[] = []): Promise<SpawnSourcePackage> {
    return loadSpawnSourceSet(ids, templates, draft, destinationId, includeGrantIdentities, enhancements);
}
async function loadSpawnSourceSet(ids: readonly string[], templates: readonly string[], draft: Row | undefined, destinationId: string | undefined,
    includeGrantIdentities: boolean, enhancements: readonly string[], catalogVersion: unknown = null): Promise<SpawnSourcePackage> {
    const sorted = (values: readonly string[]): string[] => [...new Set(values.map(id => id.trim().toUpperCase()))].sort();
    const requested = sorted([...ids, ...sourceIds(draft)]);
    templates = sorted(templates);
    enhancements = sorted(enhancements);
    const key = JSON.stringify([requested, templates, sorted([...inventoryCreationSizeTemplateIds(), ...enhancements]),
        includeGrantIdentities, spawnSourceGeneration(catalogVersion)]);
    // Destination packages contain mutable saved rows; only definition-only reads can be memoized.
    const load = (): Promise<SpawnSourcePackage> => fetchSpawnSourcePackage(requested, templates, destinationId, includeGrantIdentities, enhancements, catalogVersion);
    return destinationId ? load() : cachedSpawnSources(key, load);
}
async function fetchSpawnSourcePackage(requested: readonly string[], templates: readonly string[], destinationId: string | undefined,
    includeGrantIdentities: boolean, enhancements: readonly string[], catalogVersion: unknown): Promise<SpawnSourcePackage> {
    // The same source request prepares the status rows a newborn can carry.
    // They are catalog dependencies, rather than additional selected sources.
    const newbornStatuses = [INVENTORY_HIDDEN_ID, STATUS_IDS.summon_tag];
    const data = await withAsyncDeadline(async signal => {
        const response = await fetch(BASE_URL + 'options/api/spawn_inputs_inc.php', { method: 'POST', credentials: 'same-origin', signal,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: requested, monster_template_ids: templates,
                newborn_status_ids: newbornStatuses,
                enchantment_template_ids: [...new Set([...inventoryCreationSizeTemplateIds(), ...enhancements])], ...(destinationId ? { destination_id: destinationId } : {}) }) });
        const data: unknown = await response.json();
        if (!response.ok || !fromBaseRecord(data) || !fromBaseRecord(data.base) || !fromBaseRecord(data.metadata) || !fromBaseRecord(data.shared))
            throw new Error(fromBaseRecord(data) && typeof data.error === 'string' ? data.error : `spawn:source-read:${response.status}`);
        return data;
    }, 20_000);
    const result = data as unknown as SpawnSourcePackage;
    const definitions = mergeInventoryMetadata(result.shared, result.base).catalog;
    const held = fromBaseRecord(definitions) ? definitions : {};
    const missing = inventoryGrantSourceIds(Object.keys(held)).filter(id => !(id in held));
    if (includeGrantIdentities) for (let offset = 0; offset < missing.length; offset += 128) {
        const additional = await loadSpawnSourceSet(missing.slice(offset, offset + 128), [], undefined, destinationId, false, [], catalogVersion);
        result.base = mergeInventoryMetadata(additional.base, result.base);
        result.shared = mergeInventoryMetadata(additional.shared, result.shared);
    }

    return result;
}
/** A template edit needs only its definition, using held sources before SELECTing missing inputs. */
export async function creatureDefinitionMaximum(itemId: string, template: string | null, encounter: Row): Promise<number> {
    let data = heldSpawnPackage(encounter, [itemId.toUpperCase()]);
    const missingTemplate = template && !(Array.isArray(data?.base.monster_templates)
        && data.base.monster_templates.some(row => fromBaseRecord(row) && readItemField(row, 'monster_template_id') === template));
    if (!data || missingTemplate) {
        const selected = await loadSpawnSources(data ? [] : [itemId], template ? [template] : [], undefined, undefined, false);
        data = data ? { metadata: data.metadata, base: mergeInventoryMetadata(data.base, selected.base),
            shared: mergeInventoryMetadata(data.shared, selected.shared) } : selected;
    }
    const read = createSpawnSourceReaders({ ...data, planning_only: true }, {}, uuid, die);
    return definitionVitalityMaximum(read.creature(itemId, template), read);
}
export async function loadSpawnEncounter(id: string): Promise<Row> {
    let owner: Window = window;
    try { if (!owner.ftAdoptInventoryBase && window.parent !== window) owner = window.parent; } catch { /* cross-origin */ }
    const held = heldEncounterForAction(id, owner);
    if (held) return held;
    let response = await fetch(BASE_URL + `options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=encounter&cold=0`);
    if (response.status === 404) response = await fetch(BASE_URL + `options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=location&cold=0`);
    if (!response.ok) throw new Error(`spawn:destination-read:${response.status}`);
    const envelope: unknown = await response.json();
    if (!fromBaseRecord(envelope) || !fromBaseRecord(envelope.base)) throw new Error('spawn:destination-inputs');
    const host: Row = { _entity_base: envelope };
    if (!owner.ftAdoptInventoryBase) throw new Error('spawn:compile-bridge');
    return compileFetchedEntity(host);
}
export async function commitSpawnRows(encounterId: string, plan: SpawnRowPlan, operation = uuid()): Promise<string[]> {
    admitSpawnRowPlan(plan);
    const births = plan.writes.filter(write => write.table === 'encounter_creatures' && write.op === 'insert');
    const ids = births.map(write => String(surfaceField(write.row, 'encounter_creature_id')));
    const saved = beginSpawnSave(encounterId, ids);
    const committed = (createdIds: string[]): string[] => {
        saved();
        window.dispatchEvent(new CustomEvent('ft:spawn-committed', { detail: { encounterId, operationId: operation, ids: createdIds, get new_entities() { return plan.new_entities ?? []; } } }));
        return createdIds;
    };
    let uncertain = false;
    try {
        const templates = plan.writes.filter(write => write.table === 'enchantment_templates' && write.op === 'insert');
        for (const template of templates) {
            const id = String(readItemField(template.row, 'enchantment_template_id'));
            const members = plan.writes.filter(write => write.table === 'enchantment_template_enchantments'
                && readItemField(write.row, 'enchantment_template_id') === id);
            const response = await fetch(BASE_URL + 'options/tools/create_enchantment_template/create_enchantment_template_inc.php', {
                method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ schema: 'enchantment-authoring.v1', kind: 'template', template_id: id,
                    name: String(readItemField(template.row, 'enchantment_template_name') ?? ''), members: members.map(member => ({
                        kind: 'member', type: readItemField(member.row, 'enchantment_type'), value: readItemField(member.row, 'enchantment_item_id') })) }) });
            const result: unknown = await response.json();
            if (!response.ok || !fromBaseRecord(result) || result.ok !== true || result.template_id !== id)
                throw new Error('spawn:inventory-template-save');
        }
        await plan.prepare_new_entities?.();
        // Authored definitions are already saved; only gameplay rows belong in the spawn frame/receipt.
        if (templates.length) plan.writes = plan.writes.filter(write => !['enchantment_templates', 'enchantment_template_enchantments'].includes(write.table));
        admitSpawnRowPlan(plan);
        const held = heldEncounterForAction(encounterId), first = held && Array.isArray(held.encounter_creatures_arr) ? held.encounter_creatures_arr.find(fromBaseRecord) : null;
        const active = (window as Window & { ftGetActiveCasterId?: () => string | null }).ftGetActiveCasterId?.();
        const actorId = ecId(first ?? null) || String(active ?? encounterId).toUpperCase();
        const identity = { operation_id: operation, encounter_id: encounterId, actor_id: actorId, rev: 0, member_revs: {} };
        const applied_frame = spawnAppliedFrame(identity, plan);
        const command = { destination_id: encounterId, ...identity, plan };
        window.ftPushAppliedTruth?.({ type: 'janus_spawn_truth', command: identity, applied_frame });
        uncertain = true;
        const response = await saveAppliedTruth(BASE_URL + 'options/game/actions/spawn_rows_commit_inc.php', command, { method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command) });
        uncertain = response.ok || response.status >= 500;
        const result: unknown = await response.json();
        if (!response.ok || !fromBaseRecord(result) || result.ok !== true || !Array.isArray(result.created_ids))
            throw new Error(fromBaseRecord(result) && typeof result.error === 'string' ? result.error : `spawn:commit:${response.status}`);
        return committed(result.created_ids as string[]);
    } catch (error) {
        if (uncertain && births.length) {
            // A lost receipt may follow a committed birth. Read only these new identities once;
            // never re-post, fetch the encounter, or recompile its held rows.
            const confirmed = await Promise.all(births.map(async (write, i) => {
                try {
                    const response = await fetch(BASE_URL + `options/api/base_entity_inc.php?entity=creature&entity_id=${encodeURIComponent(ids[i]!)}&cold=0`,
                        { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
                    if (!response.ok) return false;
                    const value: unknown = await response.json();
                    const row = fromBaseRecord(value) && fromBaseRecord(value.base) ? value.base.encounter_creatures : undefined;
                    if (!fromBaseRecord(row)) return false;
                    return ['encounter_creature_id', 'encounter_id', 'item_id'].every(col =>
                        String(surfaceField(row, col) ?? '').toUpperCase() === String(surfaceField(write.row, col) ?? '').toUpperCase());
                } catch { return false; }
            }));
            if (confirmed.every(Boolean)) return committed(ids);
        }
        saved(false); throw error;
    }
}
export async function commitCreatureSpawn(encounterId: string, encounter: Row, request: CreatureSpawnRequest): Promise<string[]> {
    if (request.draft) throw new Error('spawn:draft-must-be-authored-before-save');
    if (request.point) request = { ...request, point: storedSpawnPoint(request.point) };
    const operation = uuid(), preview = stageCreatureSpawn(encounterId, encounter, request, operation);
    let handedOff = false;
    try {
        const data = await prepareCreatureSpawnSources(encounter, request);
        // Planning reads the saved roster, never the temporary display rows. The
        // final raw frame replaces them in this task, before the next paint.
        preview.clear(false);
        const read = createSpawnSourceReaders(data, encounter, uuid, die), plan = planCreatureSpawn(encounterId, encounter, request, read, preview.ids);
        const added = plan.writes.filter(write => write.table === 'encounter_creatures' && write.op === 'insert')
            .map(write => ({ ...read.creature(String(surfaceField(write.row, 'item_id')), request.monsterTemplateId ?? null), encounter_creatures: write.row }));
        const retired = Array.isArray(data.base.retired_creatures) ? (data.base.retired_creatures as Row[]).filter(row => surfaceField(row, 'item_type') === 'lair').map(row => String(surfaceField(row, 'item_id'))) : [];
        const working = { ...encounter, encounter_creatures_arr: [...Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [], ...added] };
        // An add plans the lairs of what it adds; this package cannot read the held roster's carriers (syncSpawnLairs re-syncs those).
        const own = new Set<Row>(added);
        const ready = withSpawnEntities(mergeSpawnPlans(plan, planLairRows(encounterId, working, retired, read,
            row => own.has(row) ? read.entries(row, 'lair').map(readLayerListEntryUuid) : [])), data, encounter, creatorId(), true);
        handedOff = true;
        return await commitSpawnRows(encounterId, ready, operation);
    } finally { preview.clear(!handedOff); }
}
/** Display-only newborn identities: no base adoption, worker, save or broadcast
 * until initialization has its inputs. Clear only these rows on failure. */
function stageCreatureSpawn(encounterId: string, encounter: Row, request: CreatureSpawnRequest, operation: string): { ids: string[]; clear(announce: boolean): void } {
    const envelope = fromBaseRecord(encounter._entity_base) ? encounter._entity_base : null;
    const base = envelope && fromBaseRecord(envelope.base) ? envelope.base : null;
    const catalog = fromBaseRecord(base?.catalog) ? base.catalog : fromBaseRecord(encounter.catalog) ? encounter.catalog : {};
    const selected = catalog[request.itemId.toUpperCase()];
    const definition = fromBaseRecord(selected) ? selected : {};
    const count = readItemField(definition, 'item_type') === 'lair' ? 1 : request.qty ?? 1;
    const ids: string[] = [];
    const staged = new Set<Row>();
    let displayed = false;
    const announce = (): void => {
        window.dispatchEvent(new CustomEvent('ft:entity-base-compiled', { detail: { entityId: encounterId, entity: envelope?.entity ?? 'encounter',
            baseRev: envelope?.base_rev, computed: encounter, applied: true } }));
        window.dispatchEvent(new CustomEvent('ft:inventory-base-compiled', { detail: { entityId: encounterId, inventoryUnchanged: true } }));
    };
    // Invalid requests still go through the engine's existing named refusals.
    if (fromBaseUuid(encounterId) && fromBaseUuid(request.itemId) && Number.isSafeInteger(count) && count > 0 && count <= 100
        && (!request.instanceId || count === 1 && fromBaseUuid(request.instanceId))) {
        for (let i = 0; i < count; i++) {
            const id = request.instanceId ?? uuid(); ids.push(id);
            const row = { encounter_creature_id: id, encounter_id: encounterId, item_id: request.itemId,
                encounter_creature_name: request.name || readItemField(definition, 'item_name') || 'Loading creature…',
                x_pos: request.point?.x ?? 0, y_pos: request.point?.y ?? 0, z_pos: request.point?.z ?? 0 };
            staged.add({ entity_id: id, items: definition.items ?? { item_id: request.itemId, item_type: 'creature' }, encounter_creatures: row,
                position: { x_pos: row.x_pos, y_pos: row.y_pos, z_pos: row.z_pos }, inventory: [], _entity_refusal: 'unported:creature-base-pending' });
        }
        encounter.encounter_creatures_arr = [...Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [], ...staged];
        displayed = true;
        window.dispatchEvent(new CustomEvent('ft:spawn-preview-applied', { detail: { operation_id: operation, encounter_id: encounterId, creatureIds: ids } }));
        announce();
    }
    return { ids, clear(notify) {
        if (staged.size) {
            encounter.encounter_creatures_arr = (encounter.encounter_creatures_arr as Row[]).filter(row => !staged.has(row));
            staged.clear();
        }
        if (notify && displayed) { displayed = false; announce(); }
    } };
}
/** Authoring also checks the entire requested initialization before its first write. */
export async function preflightCreatureSpawn(encounterId: string, encounter: Row, request: CreatureSpawnRequest): Promise<SpawnSourcePackage> {
    const data = await prepareCreatureSpawnSources(encounter, request);
    planCreatureSpawn(encounterId, encounter, request, createSpawnSourceReaders(data, encounter, uuid, () => 1));
    return data;
}
async function prepareCreatureSpawnSources(encounter: Row, request: CreatureSpawnRequest): Promise<SpawnSourcePackage> {
    let data = heldSpawnPackage(encounter, [request.itemId.toUpperCase()]);
    const templates = request.monsterTemplateId ? [request.monsterTemplateId] : [];
    const hasTemplate = (id: string): boolean => !!data && Array.isArray(data.base.monster_templates)
        && data.base.monster_templates.some(row => fromBaseRecord(row) && readItemField(row, 'monster_template_id') === id);
    const catalog = data && fromBaseRecord(data.base.catalog) ? data.base.catalog : {};
    const sizing = new Set(data && Array.isArray(data.base.enchantment_templates) ? data.base.enchantment_templates
        .filter(fromBaseRecord).map(row => readItemField(row, 'enchantment_template_id')) : []);
    if (!data || request.draft || templates.some(id => !hasTemplate(id))
        || request.hidden && !catalog[INVENTORY_HIDDEN_ID]
        || inventoryGrantSourceIds(Object.keys(catalog)).some(id => !(id in catalog))
        || inventoryCreationSizeTemplateIds().some(id => !sizing.has(id))) data = null;
    data = data && { ...data, planning_only: true };
    let read = data && createSpawnSourceReaders(data, encounter, uuid, () => 1);
    // Runtime bases load ranges lazily. Reuse this planning source, and SELECT
    // only when the requested creature's own possible table reads lack witnesses.
    if (!data || !read || spawnCreatureTableInputsMissing(data.base,
        read.creature(request.itemId, request.monsterTemplateId ?? null), request, read)) {
        data = { ...await loadSpawnSources([request.itemId], templates, request.draft, String(encounterField(encounter, 'encounter_id'))), planning_only: true };
        read = createSpawnSourceReaders(data, encounter, uuid, () => 1);
    }
    // Retired lairs are persisted state, never inferred from the visible roster.
    // Read them only when this add actually carries a lair and lacks that input.
    if (!Array.isArray(data.base.retired_creatures) && read.entries(read.creature(request.itemId, request.monsterTemplateId ?? null), 'lair').length) {
        const destination = await loadSpawnSources([], [], undefined, String(encounterField(encounter, 'encounter_id')), false);
        data = { ...data, base: { ...data.base, retired_creatures: destination.base.retired_creatures } };
    }
    return data;
}
export async function syncSpawnLairs(encounterId: string, encounter: Row, epoch = ''): Promise<void> {
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    // One held-reader snapshot picks the package AND drives the plan: the lair-only package cannot resolve other roster carriers.
    const lairs = new Map(roster.filter(row => readItemField(row, 'item_type') === 'creature')
        .map(row => [row, (ftLayerEntries(row, 'lair') ?? []).map(readLayerListEntryUuid).filter(fromBaseUuid)] as const));
    const ids = [...new Set([...lairs.values()].flat())];
    if (!ids.length) return;
    const present = new Set(roster.filter(row => readItemField(row, 'item_type') === 'lair').map(row => String(readItemField(row, 'item_id')).toUpperCase()));
    if (ids.every(id => present.has(id.toUpperCase()))) return;
    const data = await loadSpawnSources(ids, [], undefined, encounterId);
    const retired = Array.isArray(data.base.retired_creatures) ? (data.base.retired_creatures as Row[]).filter(row => surfaceField(row, 'item_type') === 'lair').map(row => String(surfaceField(row, 'item_id'))) : [];
    const read = createSpawnSourceReaders(data, encounter, uuid, die), plan = planLairRows(encounterId, encounter, retired, read, row => lairs.get(row) ?? []);
    if (spawnPlanHasEffects(plan)) {
        const signature = roster.map(row => [ecId(row), readItemField(row, 'item_id'), surfaceField(fromBaseRecord(row.encounter_creatures) ? row.encounter_creatures : row, 'encounter_creature_name')]).sort();
        await commitSpawnRows(encounterId, withSpawnEntities(plan, data, encounter, creatorId()), spawnRowIdentity(`lair-sync|${encounterId.toUpperCase()}|${epoch}|${JSON.stringify(signature)}`));
    }
}
type PreparedSpawnEffects = {
    definition: (id: string) => Row | null;
    transform: NonNullable<JanusReaders['spawnTransform']>;
    engineSummons: NonNullable<JanusReaders['spawnSummons']>;
    iceWallSections: NonNullable<JanusReaders['iceWallSections']>;
};
const heldSpawnPackages = new WeakMap<Row, SpawnSourcePackage>();
function heldSpawnPackage(encounter: Row, roots: readonly string[] = []): SpawnSourcePackage | null {
    const candidates = [encounter, ...Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []];
    for (const candidate of candidates) {
        const envelope = candidate._entity_base;
        if (!fromBaseRecord(envelope) || !fromBaseRecord(envelope.base) || !fromBaseRecord(envelope.metadata)) continue;
        const base = envelope.base, catalog = base.catalog;
        if (!fromBaseRecord(catalog) || !roots.every(id => Object.prototype.hasOwnProperty.call(catalog, id))) continue;
        let data = heldSpawnPackages.get(base);
        if (!data) {
            const keys = ['catalog', 'missing_definition_ids', 'monster_templates', 'monster_template_layers', 'missing_monster_template_ids',
                'enchantment_templates', 'enchantment_template_enchantments', 'missing_enchantment_template_ids',
                'enchantment_match_candidates', 'enchantment_match_rows', 'inventory_metadata',
                'definition_videos', 'random_table_rows', 'random_table_ranges', 'picture_lookup_ids', 'picture_sources', 'pl_signature', 'dependency_revs'];
            data = { base: Object.fromEntries(keys.filter(key => key in base).map(key => [key, base[key]])),
                metadata: envelope.metadata as SpawnSourcePackage['metadata'], shared: {} };
            heldSpawnPackages.set(base, data);
        }
        return data;
    }
    return null;
}
export function heldSpawnEffects(encounter: Row): PreparedSpawnEffects | null {
    const data = heldSpawnPackage(encounter); return data ? spawnEffectsFromPackage(data, encounter) : null;
}
/** Fabrication plans saved rows directly; give those births the same initial
 * compile/adoption as summons, using only SELECT inputs already held by the page. */
export function prepareCastInventoryEntities(plan: SpawnRowPlan | undefined, encounter: Row,
    heldInputs: (id: string) => Row | undefined): void {
    if (!plan?.writes.some(write => write.table === 'cast_inventory' && write.op === 'insert')) return;
    const data = heldSpawnPackage(encounter);
    if (!data) throw new Error('spawn:cast-inventory-inputs-not-held');
    let base = data.base;
    for (const source of plan.new_entity_inputs ?? []) base = mergeInventoryMetadata(base, mergeInventoryMetadata(source.shared, source.base));
    const ids = new Set(plan.writes.flatMap(write => ['inventory_item_id', 'status_item_id'].flatMap(key => {
        const id = readItemField(write.row, key); return fromBaseUuid(id) ? [id.toUpperCase()] : [];
    })));
    for (const id of ids) {
        const next = heldMemberDefinitionInputs(base, id, heldInputs(id));
        if (next) base = next;
        else if (!fromBaseRecord(base.catalog) || !fromBaseRecord(base.catalog[id])) throw new Error(`spawn:definition-unavailable:${id}`);
    }
    base = mergeInventoryMetadata(base, inventoryYieldTemplateSources(encounter));
    withSpawnEntities(plan, { ...data, base }, encounter, creatorId());
    const prepare = plan.prepare_new_entities!;
    Object.defineProperty(plan, 'prepare_new_entities', { configurable: true, value: () =>
        withAsyncDeadline(() => prepare(), 15000).catch(error => {
            if (error?.name !== 'TimeoutError') throw error;
            const reason = 'spawn:cast-inventory-preparation-timeout';
            refuseUnported(reason); throw new Error(reason);
        }) });
}
export async function prepareSpawnEffects(roots: readonly string[], templates: readonly string[], encounter: Row, selected: readonly string[] = []): Promise<PreparedSpawnEffects> {
    // spawn_inputs_inc.php reads at most 128 sources; a larger kit is refused by name, never truncated.
    if (roots.length > 128) throw new Error('spawn:source-roots-over-128');
    const held = heldSpawnPackage(encounter), data = await loadSpawnSources([...new Set([...roots, ...selected])], templates);
    // New source inputs include name/loot tables; held definitions supply the corpse's existing kit. A member read on its
    // own carries its envelope; otherwise the encounter's envelope holds every member's catalog roots (a cast uses the
    // held encounter rows, so members are no longer re-read per cast — genopitchseed).
    const encounterBase = fromBaseRecord(encounter._entity_base) && fromBaseRecord(encounter._entity_base.base) ? encounter._entity_base.base : null;
    const heldCatalog = held && fromBaseRecord(held.base.catalog) ? held.base.catalog
        : encounterBase && fromBaseRecord(encounterBase.catalog) ? encounterBase.catalog : {};
    const rawCatalog = mergeInventoryMetadata(data.shared, data.base).catalog;
    const corpse = roots.some(id => fromBaseRecord(rawCatalog) && fromBaseRecord(rawCatalog[id])
        && ftLayerValue(rawCatalog[id], 'summon_transforms_targeted_corpse') != null);
    if (!selected.length || corpse) data.base.catalog = { ...heldCatalog, ...fromBaseRecord(data.base.catalog) ? data.base.catalog : {} };
    const worker = window.ftPreviewSpawnSource;
    if (worker && selected.length) {
        data.creature_previews = Object.fromEntries(await Promise.all(selected.map(async itemId => {
            const template = templates[0] ?? null;
            return [spawnCreatureSourceKey(itemId, template), await worker({ data, itemId, template, draft: {}, sourceOnly: true })];
        })));
    }
    return spawnEffectsFromPackage(data, encounter);
}
/** Inventory factories for actual deaths share spawn's complete raw definition/compiler lane. */
type DeathRead = NonNullable<JanusReaders['deathResources']>;
type DeathPackage = { signature: string; loading: Promise<DeathRead>; read?: DeathRead; sources?: SpawnSourcePackage[]; complete?: boolean };
const deathPackages = new WeakMap<Row, DeathPackage>();
const pendingDeathReads = new WeakMap<DeathRead, Promise<DeathRead>>();
class DeathResourcesPending extends Error {
    constructor(readonly loading: Promise<unknown>) { super('Death source inputs are loading'); }
}
/** Synthetic preflight outcomes need no replacement assets; only an actual zero suspends the action. */
export function lazySpawnTransform(dryRun = false): NonNullable<JanusReaders['spawnTransform']> & { loading: () => Promise<unknown> | undefined } {
    const prepared = new Map<string, PreparedSpawnEffects>();
    let loading: Promise<PreparedSpawnEffects> | undefined;
    return Object.assign<NonNullable<JanusReaders['spawnTransform']>, { loading: () => Promise<unknown> | undefined }>((ctx, target, original, request) => {
        if (dryRun || ctx.dryRun) return emptySpawnPlan();
        // The replacement keeps the victim's kit, so the package is requested with those definitions too.
        const roots = [request.itemId, ...request.dropInventory ? [] : transformKitItemIds(original)], key = roots.join('|');
        const held = prepared.get(key);
        if (held) return held.transform(ctx, target, original, request);
        loading ??= prepareSpawnEffects(roots, [], ctx.encounter).then(value => {
            prepared.set(key, value); loading = undefined; return value;
        });
        throw new DeathResourcesPending(loading);
    }, { loading: () => loading });
}
/** A surviving hit never requests spawn inputs. An actual death resumes its exact draw prefix. */
export function lazyDeathResources(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>, campaign: Row | null = null): DeathRead & { loading: () => Promise<DeathRead> | undefined } {
    const host = typeof window === 'undefined' ? undefined : window as unknown as { FateTide?: { encounterCache?: Map<string, Row> } };
    const canonical = host?.FateTide?.encounterCache?.get(String(encounterField(encounter, 'encounter_id') ?? ''));
    const sourceEncounter = canonical ?? encounter;
    const roster = Array.isArray(sourceEncounter.encounter_creatures_arr) ? sourceEncounter.encounter_creatures_arr.filter(fromBaseRecord) : [];
    const sourceTargets = [...new Map([...roster, ...targets].map(target => [ecId(target), target])).values()];
    const prepared = deathPackages.get(sourceEncounter);
    let ready = prepared?.signature === deathSourceSignature(sourceTargets, sourceEncounter, read) ? prepared.read : undefined;
    const factory: DeathRead = (ctx, target) => {
        if (ready) return ready(ctx, target);
        const loading = pendingDeathReads.get(factory) ?? prepareDeathResources(sourceTargets, sourceEncounter, read, campaign).then(value => {
            ready = value; pendingDeathReads.delete(factory); return value;
        });
        pendingDeathReads.set(factory, loading);
        throw new DeathResourcesPending(loading);
    };
    return Object.assign(factory, { loading: () => pendingDeathReads.get(factory) });
}
export function deathResourcesPending(error: unknown): error is DeathResourcesPending { return error instanceof DeathResourcesPending; }
export function resumeDeathResources<T>(plan: () => T, suspend: () => void, read?: DeathRead): T | Promise<T> {
    return resumePlan((function* (): Generator<Promise<unknown>, T, unknown> {
        for (;;) {
            let loading: Promise<unknown>;
            try {
                const result = plan(), pending = read && pendingDeathReads.get(read);
                // Ability resolvers return diagnostics for unknown errors; the resource read still owns its suspension.
                if (!pending) return result;
                loading = pending;
            } catch (error) {
                if (!deathResourcesPending(error)) throw error;
                loading = error.loading;
            }
            suspend();
            yield loading;
        }
    })());
}
/** Source packages depend on source identities and authored definitions, never HP/AP or movement revisions. */
function deathSourceSignature(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>): string {
    const envelope = fromBaseRecord(encounter._entity_base) ? encounter._entity_base : null;
    return JSON.stringify([spawnSourceGeneration(envelope?.catalog_version),
        targets.map(target => {
            const own = String(readItemField(target, 'item_id') ?? ecLean(target, 'item_id') ?? '');
            let sources: string[];
            try { sources = deathSourceIds(target, read); } catch { sources = []; }
            return [ecId(target), own,
                readItemField(target, 'item_type') ?? ecLean(target, 'item_type'), !!read.definition?.(own), [...new Set(sources)].sort()];
        }).sort((a, b) => String(a[0]).localeCompare(String(b[0])))]);
}
function prepareDeathPackage(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>, campaign: Row | null): DeathPackage {
    const signature = deathSourceSignature(targets, encounter, read);
    const held = deathPackages.get(encounter);
    if (held?.signature === signature) return held;
    const loading = loadDeathResources(targets, encounter, read).then(result => {
        entry.complete = result.complete;
        entry.sources = result.packages;
        const resources = deathResourcesFromPackages(result.packages, encounter, read, campaign);
        if (result.complete) entry.read = resources;
        else if (deathPackages.get(encounter) === entry) deathPackages.delete(encounter);
        return resources;
    });
    const entry: DeathPackage = { signature, loading };
    deathPackages.set(encounter, entry);
    void loading.catch(() => { if (deathPackages.get(encounter)?.loading === loading) deathPackages.delete(encounter); });
    return entry;
}
export function prepareDeathResources(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>, campaign: Row | null = null): Promise<DeathRead> {
    return prepareDeathPackage(targets, encounter, read, campaign).loading;
}
/** Adoption warms a roster/source set once. Actual actions still bind readers to their own snapshot. */
export function warmDeathResources(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>, campaign: Row | null = null): Promise<void> {
    const host = globalThis as { __ftDeathResourceWarmups?: Map<string, Promise<SpawnSourcePackage[]>> };
    const jobs = host.__ftDeathResourceWarmups ??= new Map<string, Promise<SpawnSourcePackage[]>>();
    const signature = deathSourceSignature(targets, encounter, read);
    let job = jobs.get(signature);
    if (!job) {
        const entry = prepareDeathPackage(targets, encounter, read, campaign);
        job = entry.loading.then(() => {
            if (!entry.complete) throw new Error('spawn:death-source-warmup-failed');
            return entry.sources!;
        });
        jobs.set(signature, job);
        const pending = job;
        void pending.catch(() => { if (jobs.get(signature) === pending) jobs.delete(signature); });
    }
    return job.then(sources => {
        if (deathSourceSignature(targets, encounter, read) !== signature || deathPackages.get(encounter)?.signature === signature) return;
        // Rebind the current snapshot without fetching or suspending a later death action.
        const resources = deathResourcesFromPackages(sources, encounter, read, campaign);
        deathPackages.set(encounter, { signature, read: resources, loading: Promise.resolve(resources), sources, complete: true });
    });
}
async function loadDeathResources(targets: readonly Row[], encounter: Row,
    read: Pick<JanusReaders, 'value' | 'entries'> & Partial<Pick<JanusReaders, 'definition'>>): Promise<{ packages: SpawnSourcePackage[]; complete: boolean }> {
    // One unavailable target source must not block another target, healing, or a surviving hit.
    // Keep complete source packages intact: their video/table side inputs belong to each factory.
    const ids = new Set<string>(DEATH_STATUS_SOURCE_IDS), packages: SpawnSourcePackage[] = [];
    let complete = true;
    for (const target of targets) {
        try {
            const own = String(readItemField(target, 'item_id') ?? ecLean(target, 'item_id') ?? '').toUpperCase();
            const type = String(readItemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '');
            const sources = [...deathSourceIds(target, { value: (row, key) => read.value(row, key), entries: (row, key) => read.entries(row, key) }),
                ...(type === 'creature' && fromBaseUuid(own) && !read.definition?.(own) ? [own] : [])]
                .map(id => id.toUpperCase());
            for (const id of sources) ids.add(id);
        } catch { complete = false; }
    }
    const roots = [...ids].sort(), envelope = fromBaseRecord(encounter._entity_base) ? encounter._entity_base : null;
    for (let start = 0; start < roots.length; start += 128) {
        try { packages.push(await loadSpawnSourceSet(roots.slice(start, start + 128), [], undefined, undefined, true, [], envelope?.catalog_version)); }
        catch { complete = false; }
    }
    return { packages, complete };
}
function deathResourcesFromPackages(packages: SpawnSourcePackage[], encounter: Row,
    read: Partial<Pick<JanusReaders, 'definition'>>, campaign: Row | null): DeathRead {
    const heldCampaign = campaign ?? (fromBaseRecord(encounter.campaign_arr) ? encounter.campaign_arr : null);
    let birthData: SpawnSourcePackage | undefined;
    const resources: DeathRead = (ctx, target) => {
        const unavailable = () => handBack(`Death details for ${String(ecLean(target, 'encounter_creature_name') ?? 'this creature')} could not be loaded.`);
        if (!packages.length) unavailable();
        if (heldCampaign && !fromBaseRecord(ctx.encounter.campaign_arr)) ctx.encounter.campaign_arr = fromBaseClone(heldCampaign);
        const factories = packages.map(data => createSpawnSourceReaders(data, ctx.encounter, () => ctx.read.uuid(), sides => ctx.roll.d(sides), () => ctx.read.campaignClock()));
        const factoryIndex = (id: string) => {
            const key = id.toUpperCase();
            const index = packages.findIndex(data => (fromBaseRecord(data.base.catalog) && !!data.base.catalog[key])
                || (fromBaseRecord(data.shared.catalog) && !!data.shared.catalog[key]));
            if (index < 0) unavailable();
            return index;
        };
        const inventory = { uuid: () => ctx.read.uuid(), clock: factories[0]!.clock,
            item: (id: string) => factories[factoryIndex(id)]!.item(id),
            inventory: (...args: Parameters<typeof factories[number]['inventory']>) => factories[factoryIndex(String(readItemField(args[0], 'item_id')))]!.inventory(...args) };
        return { inventory, prepareRows: plan => {
            // An action can hold only the compiled surface. Climate media SELECT
            // witnesses belong to the canonical held base, never that projection.
            const held = ftCurrentTruthSink()?.heldBase?.(String(encounterField(ctx.encounter, 'encounter_id')))
                ?? (fromBaseRecord(encounter._entity_base) ? encounter._entity_base.base : null);
            return withSpawnEntities(plan, birthData ??= {
                base: packages.reduce((base, data) => mergeInventoryMetadata(base, mergeInventoryMetadata(data.shared, data.base)), {} as Row),
                shared: {}, metadata: packages[0]!.metadata,
            }, fromBaseRecord(held) ? { ...ctx.encounter, _entity_base: { base: held } } : ctx.encounter, creatorId());
        }, casterDeathStatusIds: factories[0]!.casterDeathStatusIds ?? [], definition: (id: string) => {
            const key = id.toUpperCase();
            const index = packages.findIndex(data => (fromBaseRecord(data.base.catalog) && !!data.base.catalog[key])
                || (fromBaseRecord(data.shared.catalog) && !!data.shared.catalog[key]));
            if (index < 0) return null;
            const raw = packages[index]!.base.catalog as Row;
            if (readItemField(raw?.[key] as Row, 'item_type') === 'creature') return raw[key] as Row;
            return factories[index]!.item(key);
        }, creatureSource: (id: string) => {
            const held = read.definition?.(id);
            if (held && readItemField(held, 'item_type') === 'creature') return held;
            for (const data of packages) for (const catalog of [data.base.catalog, data.shared.catalog]) {
                const raw = fromBaseRecord(catalog) ? catalog[id.toUpperCase()] : null;
                if (fromBaseRecord(raw) && readItemField(raw, 'item_type') === 'creature') return raw;
            }
            return null;
        } };
    };
    return resources;
}
function spawnEffectsFromPackage(data: SpawnSourcePackage, encounter: Row): PreparedSpawnEffects {
    const definitions = mergeInventoryMetadata(data.shared, data.base).catalog;
    const boundaryPlan = (plan: () => SpawnRowPlan, dryRun = false): SpawnRowPlan => {
        try {
            const rows = admitSpawnRowPlan(plan());
            if (dryRun) return rows;
            const held = ftCurrentTruthSink()?.heldBase?.(String(encounterField(encounter, 'encounter_id')));
            return withSpawnEntities(rows, data, held ? { ...encounter, _entity_base: { base: held } } : encounter, creatorId());
        }
        catch (error) {
            if (error instanceof HandBack) throw error;
            handBack(error instanceof Error ? error.message : 'spawn:planning-failed');
        }
    };
    return {
        iceWallSections(ctx, wall) {
            const lookup = (id: string): Row | null => fromBaseRecord(definitions) && fromBaseRecord(definitions[id.toUpperCase()])
                ? definitions[id.toUpperCase()] as Row : null;
            const catalog = (key: string): Row[] => Array.isArray(data.metadata[key]) ? data.metadata[key] as Row[] : [];
            const result = planIceWallSections(wall, String(encounterField(ctx.encounter, 'encounter_id')), {
                definition: lookup, value: ctx.read.value, uuid: ctx.read.uuid,
                sizeScale: id => readItemField(catalog('sizeCatalog').find(row => readItemField(row, 'item_id') === id) ?? null, 'vitality_scale'),
                density: id => readItemField(catalog('compositions').find(row => readItemField(row, 'item_id') === id) ?? null, 'density_lb_ft3'),
            });
            if (!ctx.dryRun) result.rows = withSpawnEntities(result.rows, data, ctx.encounter, creatorId());
            return result;
        },
        definition(id) {
            return fromBaseRecord(definitions) && fromBaseRecord(definitions[id.toUpperCase()]) ? definitions[id.toUpperCase()] as Row : null;
        },
        transform(ctx, target, original, request) { return boundaryPlan(() => {
            const working: Row = { ...ctx.encounter, encounter_creatures_arr: [...ctx.members].map(([id, member]) => id === ecId(target) ? original : member) };
            const plan = planTransformRows(String(encounterField(working as never, 'encounter_id')), working, request,
                createSpawnSourceReaders(data, working, uuid, sides => ctx.roll.d(sides)));
            (plan.logs ??= []).push({ kind: 'creature_transform', name: String(surfaceField(original.encounter_creatures as Row, 'encounter_creature_name') ?? 'Creature'), detail: '' });
            return plan;
        }, ctx.dryRun); },
        engineSummons(ctx, plans) { return boundaryPlan(() => {
            const casterId = ctx.summonOrigin?.casterId ?? plans[0]?.caster_id;
            const caster = casterId ? ctx.members.get(casterId) : null;
            if (!caster) throw new Error('spawn:caster-not-held');
            const working: Row = { ...ctx.encounter, encounter_creatures_arr: [...ctx.members].map(([id, row]) => plans.some(plan => plan.corpse === id) ? ctx.originals.get(id) ?? row : row) };
            const abilityId = ctx.summonOrigin?.abilityId ?? plans[0]?.ability_id ?? '';
            const read = createSpawnSourceReaders(data, working, uuid, sides => ctx.roll.d(sides));
            const item = read.item, abilities = new Set([abilityId, ...plans.map(plan => plan.ability_id).filter((id): id is string => !!id)]);
            // The casting spell is already held/compiled by this action; a child's source package need not carry its video inputs.
            read.item = id => abilities.has(id) ? fromBaseClone(ctx.read.definition(id) ?? item(id)) : item(id);
            return planSummonRows(String(encounterField(working as never, 'encounter_id')), working, caster,
                abilityId, plans, read);
        }, ctx.dryRun); },
    };
}
export async function previewSpawnDraft(itemId: string, draft: Row = {}, template: string | null = null): Promise<Row> {
    const data = await loadSpawnSources([itemId], template ? [template] : [], draft);
    let owner: Window = window;
    try { if (!owner.ftPreviewSpawnSource && window.parent !== window) owner = window.parent; } catch { /* cross-origin */ }
    if (!owner.ftPreviewSpawnSource) throw new Error('spawn:compile-bridge');
    const result = await owner.ftPreviewSpawnSource({ data, itemId, draft, template } satisfies SpawnPreviewInput);
    // The preview can use a cached source package, distinct from the object request
    // that initiated it. Preserve ITS revisions, never borrow that request's revisions.
    Object.defineProperty(result, '_definition_input_revs', { value: data.base.dependency_revs, configurable: true });
    return result;
}
export function spawnFailure(error: unknown): void {
    refuseUnported(error instanceof Error ? error.message : 'spawn:planning-failed');
}

export async function previewInventory(itemId: string, template = '', values: Row = {}): Promise<Row> {
    const data = await loadSpawnSources([itemId], [], undefined, undefined, true, template ? [template] : []);
    const definition = fromBaseRecord(data.base.catalog) ? data.base.catalog[itemId.toUpperCase()] : null;
    return previewInventoryFromSources(data, itemId, { inventory_id: uuid(), inventory_item_id: itemId,
        inventory_name: fromBaseRecord(definition) ? readItemField(definition, 'item_name') : '',
        item_qty: 1, possess_id: '', equipped: '', inventory_unidentified: 0, ...values, enchantment_template_id: template || null });
}
export async function commitEncounterInventorySpawn(values: Row): Promise<string> {
    const destination = String(values.encounter_id || values.location_id || '');
    if (!fromBaseUuid(destination)) throw new Error('spawn:object-destination');
    const roots = [values.inventory_item_id, values.inventory_lock, values.inventory_ability_light_id].filter(fromBaseUuid);
    const template = String(values.enchantment_template_id ?? '');
    const [data, encounter] = await Promise.all([loadSpawnSources(roots, [], undefined, destination, true, template ? [template] : []), loadSpawnEncounter(destination)]);
    let owner: Window = window;
    try { if (!owner.ftNewInventoryPlacementInputs && window.parent !== window) owner = window.parent; } catch { /* cross-origin */ }
    const placement = values.position_ft ? undefined : await withAsyncDeadline(async () => {
        if (!owner.ftNewInventoryPlacementInputs) throw new Error('placement:browser-planner-unavailable');
        return owner.ftNewInventoryPlacementInputs(values);
    }, 15000);
    const plan = planEncounterInventorySpawn(data, encounter, destination, values, { uuid, die }, placement);
    await commitSpawnRows(destination, plan);
    const root = plan.writes.find(write => write.table === 'encounter_inventory');
    if (!root) throw new Error('spawn:object-receipt-missing');
    const id = String(readInventoryField({ inventory: root.row }, 'inventory_id')), light = String(values.inventory_ability_light_id ?? '');
    // Existing UI consumers read only these UUID-valued success attributes.
    return `<span hidden data-ft-added-encounter-inventory-id="${id}" data-ft-added-ability-light-id="${fromBaseUuid(light) ? light : ''}"></span>`;
}

/** Authoring producers save these exact birth trees in their existing transaction. */
export async function planInventoryBirths(destination: string, requests: readonly { key: string; itemId: string; initialStatuses?: readonly string[];
    inventorySize?: string; curtainOpening?: Parameters<typeof planCurtainSize>[2] }[]): Promise<Record<string, SpawnRowPlan>> {
    if (!requests.length) return {};
    const [data, encounter] = await withAsyncDeadline(() => Promise.all([loadSpawnSources([...new Set([
        ...requests.map(row => row.itemId), ...requests.flatMap(row => row.initialStatuses ?? []), SPOILING_STATUS_ID,
    ])]), loadSpawnEncounter(destination)]), 15000).catch(() => { throw new Error('room-generation:initial-status-plan-unavailable'); });
    const campaign = fromBaseRecord(encounter.campaign_arr) ? encounter.campaign_arr : null;
    const campaignId = _ftCamField(campaign, 'campaign_id');
    // An authoring preview may borrow campaign lighting without belonging to its clock.
    const ownCampaign = fromBaseUuid(campaignId)
        && String(encounterField(encounter, 'encounter_master_parent_id') ?? '').toUpperCase() === campaignId.toUpperCase() ? campaign : null;
    const read = createSpawnSourceReaders({ ...data, planning_only: true }, { campaign_arr: ownCampaign }, uuid, die);
    const result: Record<string, SpawnRowPlan> = {};
    for (const request of requests) {
        let definition: Row;
        try { definition = read.item(request.itemId); }
        catch { throw new Error('item-compile:definition-unavailable'); }
        const plan = read.inventory(definition, { encounters: { encounter_id: destination }, inventory: [] }, 1, '', true);
        for (const write of plan.writes) {
            if (write.table === 'inventory' && readInventoryField({ inventory: write.row }, 'possess_id') === destination) write.table = 'encounter_inventory';
            // Authoring inventory is born public, as in add_inventory; status ownership/privacy is derived.
            if (write.table !== 'statuses') write.row.inventory_private = 0;
            for (const key of write.table === 'statuses' ? ['status_campaign_id', 'status_private', 'status_deleted'] : ['inventory_deleted']) delete write.row[key];
        }
        if (plan.writes.filter(write => write.table === 'encounter_inventory').length !== 1)
            throw new Error('inventory-birth:single-placement-root-required');
        const root = plan.writes.find(write => write.table === 'encounter_inventory')!;
        const sizePlan = request.curtainOpening ? planCurtainSize(null, definition, request.curtainOpening)
            : request.inventorySize ? planObjectSize(null, definition, request.inventorySize) : null;
        if (sizePlan) {
            Object.assign(root.row, sizePlan);
            Object.assign(plan, { sizePlan });
        }
        for (const status of request.initialStatuses ?? []) appendInitialInventoryStatus(plan, read, encounter, status);
        result[request.key] = plan;
    }
    return result;
}

// The authoring mount holds this session; additional requests contain only new
// definitions and never clone the already held spawn source package.
if (typeof window !== 'undefined') window.ftCreateEquipmentPreviewSession = (id, draft) =>
    createEquipmentPreviewSession(id, draft, (ids, templates, enhancements) =>
        fetchSpawnSourcePackage(ids, templates, undefined, true, enhancements, null), async input => {
            let owner: Window = window;
            try { if (!owner.ftPreviewSpawnSource && window.parent !== window) owner = window.parent; } catch { /* cross-origin */ }
            if (!owner.ftPreviewSpawnSource) throw new Error('spawn:compile-bridge');
            return owner.ftPreviewSpawnSource(input);
        });

declare global { interface Window {
    ftCreateEquipmentPreviewSession?: (id: string, draft: Row) => Promise<import('./equipment-preview-session').EquipmentPreviewSession>;
} }

// Register with the existing spawn consumers; the compile bridge keeps its own
// identity independent of gameplay and authoring planners.
if (typeof window !== 'undefined') {
    window.ftSpawnBrowser = { previewSpawnDraft, previewInventory, commitEncounterInventorySpawn, commitCreatureSpawn, loadSpawnEncounter, preflightCreatureSpawn, syncSpawnLairs, planInventoryBirths, loadSpawnSources, creatureDefinitionMaximum };
    window.ftPreviewSpawnDraft = previewSpawnDraft;
    if (typeof window.dispatchEvent === 'function') window.dispatchEvent(new CustomEvent('ft:spawn-browser-ready'));
}

declare global {
    interface Window {
        ftPreviewSpawnDraft?: typeof previewSpawnDraft;
        ftSpawnBrowser?: Pick<typeof import('./spawn-browser'), 'previewSpawnDraft' | 'previewInventory' | 'commitEncounterInventorySpawn' | 'commitCreatureSpawn' | 'loadSpawnEncounter' | 'preflightCreatureSpawn' | 'syncSpawnLairs' | 'planInventoryBirths' | 'loadSpawnSources' | 'creatureDefinitionMaximum'>;
    }
}
